import React, { useEffect, useMemo, useState } from "react";
import "./Scoreboard.css";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  (process.env.NODE_ENV === "development"
    ? "http://localhost:5001"
    : "https://kaushik-olympics-backend.mshik3.workers.dev");

interface Score {
  id: number;
  name: string;
  points: number;
  totalScore: number;
}

// Format points to at most 2 decimals; drop trailing zeros and decimal point when unnecessary
function formatPoints(value: number): string {
  if (!Number.isFinite(value)) return "0";
  const rounded = Math.round(value * 100) / 100; // limit precision to 2 decimals
  const asFixed = rounded.toFixed(2);
  return asFixed.replace(/\.0+$/, "").replace(/\.(\d*?)0+$/, ".$1");
}

const Scoreboard: React.FC = () => {
  const [scores, setScores] = useState<Score[]>([]);
  const [activeTab, setActiveTab] = useState<"team" | "individual">("team");
  const [teams, setTeams] = useState<{
    names: { A: string; B: string };
    members: { A: string[]; B: string[] };
  } | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchScores = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/scores`);
        const data: Score[] = await response.json();
        data.sort((a, b) => b.totalScore - a.totalScore);
        if (!cancelled) setScores(data);
      } catch (error) {
        console.error("Error fetching scores:", error);
      }
    };
    const fetchTeams = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/teams`);
        const data = await response.json();
        if (!cancelled) setTeams(data);
      } catch (error) {
        console.error("Error fetching teams:", error);
      }
    };

    // initial fetch
    fetchScores();
    fetchTeams();

    // react to in-app admin updates immediately
    const handleImmediateTeams = () => fetchTeams();
    const handleImmediateScores = () => fetchScores();
    window.addEventListener("teams-updated", handleImmediateTeams);
    window.addEventListener("scores-updated", handleImmediateScores);

    return () => {
      cancelled = true;
      window.removeEventListener("teams-updated", handleImmediateTeams);
      window.removeEventListener("scores-updated", handleImmediateScores);
    };
  }, []);

  const memberToScore = useMemo(() => {
    const map = new Map<string, number>();
    for (const s of scores) {
      map.set(s.name, s.totalScore);
    }
    return map;
  }, [scores]);

  const totals = useMemo(() => {
    if (!teams) return { A: 0, B: 0 };
    const sum = (arr: string[]) =>
      arr.reduce((acc, name) => acc + (memberToScore.get(name) || 0), 0);
    return {
      A: sum(teams.members.A || []),
      B: sum(teams.members.B || []),
    };
  }, [teams, memberToScore]);

  const TopThree: React.FC<{ scores: Score[] }> = ({ scores }) => {
    const reorderedScores = [scores[1], scores[0], scores[2]]; // 2nd, 1st, 3rd
    return (
      <div className="top-three">
        {reorderedScores.map((player, index) => (
          <div
            key={player.name}
            className={`top-score top-score-${index}`}
            style={{ flexGrow: index === 1 ? 2 : 3 }}
          >
            <img
              src={`/images/${
                index === 1 ? "first" : index === 0 ? "second" : "third"
              }-place.png`}
              alt={`${
                index === 1 ? "First" : index === 0 ? "Second" : "Third"
              } Place`}
              style={{
                height: index === 1 ? "150px" : "100px",
                width: index === 1 ? "150px" : "100px",
                objectFit: "contain",
              }}
              decoding="async"
              loading="lazy"
            />
            <div className="placeName">{player.name}</div>
            <div className="topPoints">
              {formatPoints(player.totalScore)} points
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ImageRow component temporarily removed - will be updated with Kaushik's photos later
  // const ImageRow: React.FC = () => {
  //   return (
  //     <div className="image-row">
  //       {[1, 2, 3].map((num) => (
  //         <div
  //           key={num}
  //           className="image-box"
  //           style={{
  //             backgroundImage: `url('/images/kaushik-${num}.png')`,
  //           }}
  //         />
  //       ))}
  //     </div>
  //   );
  // };

  return (
    <div className="container">
      <h1 className="title">Kaushik Olympics</h1>
      {scores.length >= 3 && <TopThree scores={scores.slice(0, 3)} />}

      <div className="tabs">
        <button
          className={activeTab === "team" ? "tab active" : "tab"}
          onClick={() => setActiveTab("team")}
        >
          Team
        </button>
        <button
          className={activeTab === "individual" ? "tab active" : "tab"}
          onClick={() => setActiveTab("individual")}
        >
          Individual
        </button>
      </div>

      {activeTab === "team" && teams && (
        <div className="team-view">
          <div className="team-card">
            <details>
              <summary className="team-header">
                <span className="team-toggle">
                  <span className="chev" aria-hidden="true">
                    <svg
                      viewBox="0 0 24 24"
                      focusable="false"
                      aria-hidden="true"
                    >
                      <path
                        d="M8 4l8 8-8 8"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </span>
                <div className="team-name">{teams.names.A}</div>
                <div className="team-total">{formatPoints(totals.A)} pts</div>
              </summary>
              <div id="team-A-members" className="team-members">
                {(teams.members.A || []).map((name) => (
                  <div key={name} className="score">
                    <span className="name">{name}</span>
                    <span className="points">
                      {formatPoints(memberToScore.get(name) || 0)} pts
                    </span>
                  </div>
                ))}
              </div>
            </details>
          </div>
          <div className="team-card">
            <details>
              <summary className="team-header">
                <span className="team-toggle">
                  <span className="chev" aria-hidden="true">
                    <svg
                      viewBox="0 0 24 24"
                      focusable="false"
                      aria-hidden="true"
                    >
                      <path
                        d="M8 4l8 8-8 8"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </span>
                <div className="team-name">{teams.names.B}</div>
                <div className="team-total">{formatPoints(totals.B)} pts</div>
              </summary>
              <div id="team-B-members" className="team-members">
                {(teams.members.B || []).map((name) => (
                  <div key={name} className="score">
                    <span className="name">{name}</span>
                    <span className="points">
                      {formatPoints(memberToScore.get(name) || 0)} pts
                    </span>
                  </div>
                ))}
              </div>
            </details>
          </div>
        </div>
      )}

      {activeTab === "individual" && (
        <div className="score-list">
          {scores.map((player) => (
            <div key={player.name} className="score">
              <span className="name">{player.name}</span>
              <span className="points">
                {formatPoints(player.totalScore)} pts
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Scoreboard;
